import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { ReportProblemOutlined } from '@mui/icons-material'
import { Card, CardContent, CardHeader, Chip, Divider, Link as MuiLink, Stack, Typography } from '@mui/material'

import { GameBlueprintReference, BlueprintFamilyDataIssues } from '../../hooks/useBlueprintFamilyUsage'

type BlueprintFamilyDataIssuesCardProps = {
  dataIssues: BlueprintFamilyDataIssues
}

/**
 * Lists data quality issues found in the Blueprint Family taggings:
 * game-level blueprints without a Blueprint Family, game-level blueprints
 * tagged to a discontinued Blueprint Family and Blueprint Families
 * with no linked games.
 */
export const BlueprintFamilyDataIssuesCard: FC<BlueprintFamilyDataIssuesCardProps> = ({ dataIssues }) => {
  const { t } = useTranslation()
  const { untaggedGameBlueprints, blueprintsTaggedToDiscontinued, unusedBlueprintFamilies } = dataIssues
  const issueCount = untaggedGameBlueprints.length + blueprintsTaggedToDiscontinued.length + unusedBlueprintFamilies.length

  return (
    <Card sx={{ marginTop: 3 }}>
      <CardHeader
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <ReportProblemOutlined color={issueCount > 0 ? 'warning' : 'disabled'} />
            <span>{t('internal-live-events:data_issues')}</span>
            <Chip size="small" color={issueCount > 0 ? 'warning' : 'default'} sx={{ margin: 0 }} label={issueCount} />
          </Stack>
        }
      />
      <Divider />
      <CardContent>
        {issueCount === 0 ? (
          <Typography variant="body1" color="text.secondary">
            {t('internal-live-events:no_data_issues_found')}
          </Typography>
        ) : (
          <Stack spacing={3}>
            <DataIssueSection title={t('internal-live-events:untagged_game_blueprints')} count={untaggedGameBlueprints.length}>
              {untaggedGameBlueprints.map(({ trackedGame, blueprint }) => (
                <GameBlueprintIssueRow key={`${trackedGame.game.id}-${blueprint.blueprintId}`} trackedGame={trackedGame} blueprint={blueprint} />
              ))}
            </DataIssueSection>

            <DataIssueSection title={t('internal-live-events:blueprints_tagged_to_discontinued')} count={blueprintsTaggedToDiscontinued.length}>
              {blueprintsTaggedToDiscontinued.map(({ trackedGame, blueprint, blueprintFamily }) => (
                <GameBlueprintIssueRow
                  key={`${trackedGame.game.id}-${blueprint.blueprintId}`}
                  trackedGame={trackedGame}
                  blueprint={blueprint}
                  suffix={blueprintFamily.names.en}
                />
              ))}
            </DataIssueSection>

            <DataIssueSection title={t('internal-live-events:unused_blueprint_families')} count={unusedBlueprintFamilies.length}>
              {unusedBlueprintFamilies.map((blueprintFamily) => (
                <Typography key={blueprintFamily.id} variant="body2">
                  <MuiLink component={Link} to={`/internal/live-events-tracking/blueprint-families/edit/${blueprintFamily.id}`}>
                    {blueprintFamily.names.en}
                  </MuiLink>
                </Typography>
              ))}
            </DataIssueSection>
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}

const DataIssueSection: FC<{ title: string; count: number; children?: ReactNode }> = ({ title, count, children }) => {
  if (count === 0) return null

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Chip size="small" color="warning" sx={{ margin: 0 }} label={count} />
      </Stack>
      {children}
    </Stack>
  )
}

const GameBlueprintIssueRow: FC<GameBlueprintReference & { suffix?: string }> = ({ trackedGame, blueprint, suffix }) => {
  return (
    <Typography variant="body2">
      <MuiLink component={Link} to={`/internal/live-events-tracking/events/${trackedGame.game.id}/blueprints/edit/${blueprint.blueprintId}`}>
        {trackedGame.game.name} — {blueprint.names.en}
      </MuiLink>
      {suffix ? ` (${suffix})` : ''}
    </Typography>
  )
}
